import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { createSpeechRecognition, isSpeechRecognitionSupported } from '../lib/voice';
import { aiApi } from '../lib/api';

/**
 * VoiceRecorder using Web Speech API (free, native browser).
 * - Records voice in German
 * - Shows live transcript
 * - On stop, calls AI to structure into professional description
 *
 * Props:
 *  - onStructured(structuredText, transcript, fields) - called when user accepts
 *  - vehicle, accidentDate, accidentTime - context for AI
 */
export default function VoiceRecorder({ onStructured, vehicle, accidentDate, accidentTime, testid = 'voice-recorder' }) {
  const supported = isSpeechRecognitionSupported();
  const recognitionRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [processing, setProcessing] = useState(false);
  const [structured, setStructured] = useState('');

  useEffect(() => () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
    }
  }, []);

  const start = () => {
    const r = createSpeechRecognition('de-DE');
    if (!r) {
      toast.error('Spracherkennung wird in diesem Browser nicht unterstützt');
      return;
    }
    recognitionRef.current = r;
    let final = '';
    r.onresult = (event) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const res = event.results[i];
        if (res.isFinal) final += res[0].transcript + ' ';
        else interimText += res[0].transcript;
      }
      setTranscript(final.trim());
      setInterim(interimText);
    };
    r.onerror = (e) => {
      console.warn('SpeechRecognition error', e);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        toast.error('Mikrofonzugriff verweigert. Bitte in Browser-Einstellungen erlauben.');
      } else if (e.error === 'no-speech') {
        toast.info('Keine Sprache erkannt. Bitte erneut versuchen.');
      } else {
        toast.error('Spracherkennung fehlgeschlagen: ' + e.error);
      }
      setRecording(false);
    };
    r.onend = () => setRecording(false);
    try {
      r.start();
      setRecording(true);
      setInterim('');
      if (!transcript) setStructured('');
    } catch (e) {
      toast.error('Aufnahme konnte nicht gestartet werden');
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch (e) { /* ignore */ }
    setRecording(false);
  };

  const restructure = async () => {
    if (!transcript || transcript.length < 3) {
      toast.error('Bitte erst aufnehmen');
      return;
    }
    setProcessing(true);
    try {
      const { data } = await aiApi.voiceToReport({
        transcript,
        vehicle,
        accident_date: accidentDate || undefined,
        accident_time: accidentTime || undefined,
      });
      setStructured(data.structured_description || transcript);
      onStructured?.(data.structured_description || transcript, transcript, data.structured_fields || {});
      toast.success('KI hat Beschreibung strukturiert');
    } catch (e) {
      toast.error('KI-Strukturierung fehlgeschlagen');
    } finally {
      setProcessing(false);
    }
  };

  const clear = () => {
    setTranscript('');
    setInterim('');
    setStructured('');
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <Card className="rounded-2xl border-[hsl(var(--primary))]/20 bg-gradient-to-br from-sky-50/70 to-white">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center"><Mic className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="font-heading font-semibold text-slate-900">Unfall sprechen statt tippen</div>
              <div className="text-xs text-slate-600">Erzählen Sie, was passiert ist — die KI verwandelt es in eine professionelle Beschreibung.</div>
              {!supported && <div className="text-xs text-red-600 mt-1">Hinweis: Ihr Browser unterstützt keine Live-Spracherkennung. Sie können den Text aber manuell eingeben.</div>}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {!recording ? (
              <Button type="button" onClick={start} disabled={!supported} className="bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" data-testid="voice-start-button">
                <Mic className="h-4 w-4 mr-1.5" /> Aufnahme starten
              </Button>
            ) : (
              <Button type="button" onClick={stop} className="bg-red-600 hover:bg-red-700 text-white" data-testid="voice-stop-button">
                <Square className="h-4 w-4 mr-1.5" /> Aufnahme stoppen
              </Button>
            )}
            <Button type="button" variant="outline" onClick={restructure} disabled={processing || !transcript} data-testid="voice-structure-button">
              {processing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              {processing ? 'KI verarbeitet...' : 'Mit KI strukturieren'}
            </Button>
            {(transcript || structured) && (
              <Button type="button" variant="ghost" onClick={clear} data-testid="voice-clear-button" className="text-slate-600">
                <Trash2 className="h-4 w-4 mr-1.5" /> Verwerfen
              </Button>
            )}
          </div>

          {recording && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-red-600">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" /></span>
              Aufnahme läuft … sprechen Sie jetzt
            </div>
          )}
        </CardContent>
      </Card>

      {(transcript || interim) && (
        <div>
          <div className="text-xs text-slate-500 mb-1">Erkanntes Transkript</div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800" data-testid="voice-transcript">
            {transcript}
            {interim && <span className="text-slate-500"> {interim}</span>}
          </div>
        </div>
      )}

      {structured && (
        <div>
          <div className="text-xs text-slate-500 mb-1 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> KI-strukturierte Beschreibung (editierbar)</div>
          <Textarea
            value={structured}
            onChange={(e) => { setStructured(e.target.value); onStructured?.(e.target.value, transcript, {}); }}
            rows={4}
            className="rounded-xl"
            data-testid="voice-structured-text"
          />
        </div>
      )}
    </div>
  );
}
